import React, {useCallback, useEffect, useState} from 'react';
import MicrolessonsListUI from '../view/MicrolessonsListUI';
import {inject, observer} from 'mobx-react';
import MicrolessonsListStore from '../store/MicrolessonsListStore';
import {useNavigate, useParams} from 'react-router-dom';
import {MicrolessonListItem} from '../../../../data/models/Microlessons/MicrolessonListItem';
import {CreateMicrolesson} from '../../../../modules/CreateMicrolesson';
import CreateMicrolessonStore from '../../../../modules/CreateMicrolesson/store/CreateMicrolessonStore';
import ConfirmationModal from '../../../../uiToolkit/ConfirmationModal';
import {microlessonsListText} from '../microlessonsListText';

interface Props {
  microlessonsListStore?: MicrolessonsListStore;
  createMicrolessonStore?: CreateMicrolessonStore;
}

const MicrolessonsListContainer = inject(
  'microlessonsListStore',
  'createMicrolessonStore',
)(
  observer(({microlessonsListStore, createMicrolessonStore}: Props) => {
    const [lessons, setLessons] = useState<MicrolessonListItem[]>([]);
    const [lessonToDeleteId, setLessonToDeleteId] = useState<string>();
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [pagesCount, setPagesCount] = useState(1);

    const navigate = useNavigate();
    const {organizationId} = useParams();

    useEffect(() => {
      fetchLessons();
    }, [search]);

    const fetchLessons = () => {
      if (organizationId) {
        setPage(0);
        microlessonsListStore!
          .getMicrolessons(organizationId, 0, search)
          .then(res => {
            setLessons(res.data);
            setPagesCount(res.pagesCount);
          });
      }
    };

    const onEditLesson = (id: string) => {
      navigate(`/editor/${organizationId}/microlessons/${id}`);
    };

    const onPreviewLesson = (id: string) => {
      navigate(`/preview/${organizationId}/microlesson/${id}`);
    };

    const onCreateLesson = () => {
      createMicrolessonStore!.openWizard(fetchLessons);
    };

    const onEditLessonInfo = (lessonId: string) => {
      createMicrolessonStore!.openWizard(fetchLessons, lessonId);
    };

    const onEditLessonVideo = (id: string) => {
      navigate(`/manage/${organizationId}/videoEditor/${id}`);
    };

    const onDelete = (id: string) => {
      setLessonToDeleteId(id);
    };

    const onConfirmDelete = () => {
      if (organizationId && lessonToDeleteId) {
        microlessonsListStore!
          .deleteMicrolesson(organizationId, lessonToDeleteId)
          .then(success => {
            if (success) {
              fetchLessons();
            }
            setLessonToDeleteId(undefined);
          });
      }
    };

    const loadNextPage = useCallback(async () => {
      if (organizationId && page + 1 <= pagesCount) {
        const result = await microlessonsListStore!.getMicrolessons(
          organizationId,
          page + 1,
          search,
        );
        setPagesCount(result.pagesCount);
        setLessons(oldLessons => [...oldLessons, ...result.data]);
        setPage(page + 1);
      }
    }, [pagesCount, page]);

    return (
      <>
        <MicrolessonsListUI
          lessons={lessons}
          onEditLesson={onEditLesson}
          onPreviewLesson={onPreviewLesson}
          onCreateLesson={onCreateLesson}
          onEditLessonInfo={onEditLessonInfo}
          onEditLessonVideo={onEditLessonVideo}
          onDelete={onDelete}
          loadNextPage={loadNextPage}
          isLoading={microlessonsListStore!.isLoading}
          setSearch={setSearch}
          search={search}
        />
        <ConfirmationModal
          shown={lessonToDeleteId !== undefined}
          onAccept={onConfirmDelete}
          onCancel={() => setLessonToDeleteId(undefined)}
          title={microlessonsListText.deleteLesson}
          acceptText={microlessonsListText.delete}
        />
        <CreateMicrolesson />
      </>
    );
  }),
);

export default MicrolessonsListContainer;
